import { ReactNode, useMemo } from 'react';
import { faAngleLeft, faAngleRight } from '~/assets';
import { Table } from '~/components';
import { LoadingIndicator, isEmpty } from '~/modules';
import { NumberPage } from '~/utils';
import If from '../If';
import {
  AngleIcon,
  BoxPage,
  Container,
  EmptyComponentWrapper,
  EmptyDataComponent,
  LoadingBox,
  Title,
  WrapperIcon,
  WrapperPagination,
} from './styles';

type Props<ListItem> = {
  columns?: Array<string>;
  list?: Array<ListItem>;
  totalPages?: number;
  currentPage?: number;
  isLoading?: boolean;
  showPagination?: boolean;
  isPaginationDisabled?: boolean;
  titleEmptyData: string;
  descriptionEmptyData?: string;
  renderList(list: ListItem, index: number): ReactNode;
  onChangePage?(page: number): void;
};
const PaginatedList = <ListItem,>({
  list = [],
  columns = [],
  currentPage = 0,
  totalPages = 0,
  isLoading,
  showPagination = true,
  isPaginationDisabled = false,
  titleEmptyData,
  descriptionEmptyData,
  renderList,
  onChangePage,
}: Props<ListItem>) => {
  const onPageChange = (page: number) => onChangePage && onChangePage(page);

  const validationNext = (page: number) => {
    currentPage !== totalPages && onPageChange(page);
  };

  const validationPrevious = (page: number) => {
    currentPage !== NumberPage.ADD && onPageChange(page);
  };

  const makePagination = (pageNumber: number) => {
    return (
      <BoxPage
        key={pageNumber}
        currentPage={currentPage === pageNumber}
        role="button"
        aria-label={`Ir para página ${pageNumber}`}
        aria-current={currentPage === pageNumber ? 'page' : undefined}
        onClick={() =>
          !isPaginationDisabled &&
          currentPage !== pageNumber &&
          onPageChange(pageNumber)
        }
      >
        <Title currentPage={currentPage === pageNumber}>{pageNumber}</Title>
      </BoxPage>
    );
  };

  const getPagination = () => {
    const maxPages = 10;
    const isTotalPagesMoreThanMaxPages = totalPages > maxPages;
    const pagesLength = isTotalPagesMoreThanMaxPages ? maxPages : totalPages;
    if (!isTotalPagesMoreThanMaxPages) {
      return Array.from({ length: pagesLength }).map((_, index) =>
        makePagination(index + NumberPage.ADD),
      );
    }
    const pagesList = [];
    const pagesNumberBetween = maxPages - 3;
    /* @ts-ignore */
    pagesList.push(makePagination(NumberPage.ADD));
    Array.from({ length: pagesNumberBetween }).forEach((_, index) => {
      const pageNumber = index + currentPage - 1;
      const isLastIndex = index + 1 === pagesNumberBetween;
      if (index === 0 && pageNumber > NumberPage.SECOND_PAGE) {
        /* @ts-ignore */
        pagesList.push(<Title>...</Title>);
      }
      if (pageNumber < totalPages && pageNumber > NumberPage.ADD) {
        /* @ts-ignore */
        pagesList.push(makePagination(pageNumber));
      }
      if (isLastIndex && pageNumber < totalPages) {
        /* @ts-ignore */
        pagesList.push(<Title>...</Title>);
      }
    });
    /* @ts-ignore */
    pagesList.push(makePagination(totalPages));
    return pagesList;
  };

  const isLeftIconDisabled = useMemo(
    () => currentPage === NumberPage.ADD || isPaginationDisabled,
    [currentPage, isPaginationDisabled],
  );
  const isRightIconDisabled = useMemo(
    () => currentPage >= totalPages || isPaginationDisabled,
    [currentPage, totalPages, isPaginationDisabled],
  );

  return (
    <Container>
      <Table.Wrapper>
        <Table.Root>
          <If condition={!isEmpty(columns)}>
            <Table.THead>
              <Table.Tr>
                {columns.map((headerItem) => (
                  <Table.Th key={`column-${headerItem}`}>{headerItem}</Table.Th>
                ))}
              </Table.Tr>
            </Table.THead>
          </If>

          <Table.TBody>
            <If condition={!isEmpty(list) && !isLoading}>
              {list.map((itemList, index) => renderList(itemList, index))}
            </If>
            <If condition={(isEmpty(list) && !isLoading) || !!isLoading}>
              <Table.Tr>
                <Table.Td colSpan={columns.length}>
                  <If condition={isEmpty(list) && !isLoading}>
                    <EmptyComponentWrapper>
                      <EmptyDataComponent
                        title={titleEmptyData}
                        description={descriptionEmptyData || ''}
                      />
                    </EmptyComponentWrapper>
                  </If>
                  <If condition={!!isLoading}>
                    <LoadingBox>
                      <LoadingIndicator />
                    </LoadingBox>
                  </If>
                </Table.Td>
              </Table.Tr>
            </If>
          </Table.TBody>
        </Table.Root>
      </Table.Wrapper>

      <If condition={showPagination}>
        <WrapperPagination>
          <WrapperIcon
            onClick={() =>
              !isLeftIconDisabled &&
              validationPrevious(currentPage + NumberPage.SUBTRACTION)
            }
            disabled={isLeftIconDisabled}
          >
            <AngleIcon icon={faAngleLeft} />
          </WrapperIcon>
          {getPagination()}
          <WrapperIcon
            onClick={() =>
              !isRightIconDisabled &&
              validationNext(currentPage + NumberPage.ADD)
            }
            disabled={isRightIconDisabled}
          >
            <AngleIcon icon={faAngleRight} />
          </WrapperIcon>
        </WrapperPagination>
      </If>
    </Container>
  );
};

export default PaginatedList;
